import React, { useState, useEffect, useCallback } from 'react'
import { Button, Modal, Form, Alert, Tooltip, theme } from 'antd'
import VoiceTextarea from './VoiceTextarea'
import { CheckCircleOutlined, LockOutlined, RollbackOutlined, WarningOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'

interface RDATransitionAvailable {
  to_status_id: number
  to_status_name: string
  requires_reason: boolean
  richiede_commento_stato: boolean
  description?: string
}

interface RDAStatus {
  id: number
  nome: string
  ordine: number
}

interface RDAAuditEntry {
  id: number
  from_state: string
  to_state: string
  executed_by_id: number
  user_role?: string
  reason?: string
  created_at: string
}

interface RDAStateTransitionProps {
  rdaId: number
  currentStatusId: number
  currentStatusName: string
  motivazioneRifiuto?: string | null
  commentoStato?: string | null
  dataConsegnaPrevista?: string | null
  /** Perche' si e' ordinato senza data. Campo suo, non commento_stato. */
  motivoDerogaData?: string | null
  onStateChange?: (newStatusId: number, newStatusName: string, commentoStato?: string | null) => void
}

const TERMINAL_NAMES = ['annullat', 'rifiutat']
const isTerminal = (nome: string) => TERMINAL_NAMES.some(t => nome.toLowerCase().includes(t))

const isOrdinataTransition = (t: RDATransitionAvailable) =>
  t.to_status_name.toLowerCase().includes('ordinat')

// Confronto sul NOME dello stato (coerente con isTerminal e isOrdinataTransition).
// ⚠️ Gli stati sono configurabili dalle Impostazioni: se un admin rinomina "Ricevuta"
// in qualcosa che non contiene "ricev", il badge tornerebbe cliccabile.
const isRicevutaStatus = (nome: string) => nome.toLowerCase().includes('ricev')

const RDAStateTransition: React.FC<RDAStateTransitionProps> = ({
  rdaId,
  currentStatusId,
  currentStatusName,
  motivazioneRifiuto,
  commentoStato,
  dataConsegnaPrevista,
  motivoDerogaData,
  onStateChange,
}) => {
  const [transitions, setTransitions] = useState<RDATransitionAvailable[]>([])
  const [allStatuses, setAllStatuses] = useState<RDAStatus[]>([])
  const [auditTrail, setAuditTrail] = useState<RDAAuditEntry[]>([])
  const [transitionInProgress, setTransitionInProgress] = useState(false)
  const [form] = Form.useForm()
  const [commentoForm] = Form.useForm()
  const [derogaForm] = Form.useForm()
  const [selectedTransition, setSelectedTransition] = useState<RDATransitionAvailable | null>(null)
  const [reasonModalVisible, setReasonModalVisible] = useState(false)
  const [commentoModalVisible, setCommentoModalVisible] = useState(false)
  const [derogaModalVisible, setDerogaModalVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { token } = theme.useToken()

  const loadTransitions = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/purchase-requests/${rdaId}/available-transitions`)
      setTransitions(res.data.available_transitions ?? [])
      setAllStatuses(res.data.all_statuses ?? [])
    } catch {
      setTransitions([])
      setAllStatuses([])
    }
  }, [rdaId, currentStatusId])

  const loadAuditTrail = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/purchase-requests/${rdaId}/audit`)
      setAuditTrail(res.data ?? [])
    } catch {
      setAuditTrail([])
    }
  }, [rdaId])

  useEffect(() => {
    loadTransitions()
    loadAuditTrail()
  }, [loadTransitions, loadAuditTrail])

  const handleTransitionClick = (t: RDATransitionAvailable) => {
    setSelectedTransition(t)

    if (isOrdinataTransition(t)) {
      const dataValida = dataConsegnaPrevista && dayjs(dataConsegnaPrevista).isAfter(dayjs())
      if (!dataValida) {
        derogaForm.resetFields()
        setDerogaModalVisible(true)
        return
      }
    }

    if (t.requires_reason) {
      form.resetFields()
      setReasonModalVisible(true)
    } else if (t.richiede_commento_stato) {
      commentoForm.resetFields()
      setCommentoModalVisible(true)
    } else {
      performTransition(t, null, null)
    }
  }

  const performTransition = async (
    t: RDATransitionAvailable,
    reason: string | null,
    commentoStato: string | null,
    motivoDeroga: string | null = null,
  ) => {
    try {
      setTransitionInProgress(true)
      setError(null)
      // La deroga viaggia in un campo suo. Prima finiva in commento_stato e il
      // backend la buttava via, perche' Ordinata non richiede un commento di
      // stato: la motivazione obbligatoria spariva senza che nessuno lo sapesse.
      //
      // `data_consegna_prevista` viaggia per la stessa ragione: e' la data che
      // l'utente ha davanti, non ancora salvata. Senza, la guardia qui sopra e
      // il vincolo lato server guardavano due dati diversi, e chi compilava la
      // data e premeva subito il pulsante di stato si vedeva rifiutare una
      // transizione che a schermo era legittima. Il server la valuta e, se e'
      // valida, la salva: cosi' non resta solo nel browser.
      await axiosInstance.post(`/purchase-requests/${rdaId}/transition/${t.to_status_id}`, {
        reason,
        commento_stato: commentoStato,
        motivo_deroga_data: motivoDeroga,
        data_consegna_prevista: dataConsegnaPrevista ?? null,
      })
      setReasonModalVisible(false)
      setCommentoModalVisible(false)
      setDerogaModalVisible(false)
      setSelectedTransition(null)
      form.resetFields()
      commentoForm.resetFields()
      derogaForm.resetFields()
      if (onStateChange) onStateChange(t.to_status_id, t.to_status_name, commentoStato)
      loadTransitions()
      loadAuditTrail()
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Errore durante la transizione'
      setError(msg)
      setCommentoModalVisible(false)
      setReasonModalVisible(false)
      setDerogaModalVisible(false)
      Modal.error({ title: 'Errore transizione', content: msg })
    } finally {
      setTransitionInProgress(false)
    }
  }

  const transitionMap = new Map(transitions.map(t => [t.to_status_id, t]))

  const visibleStatuses = allStatuses.filter(s => {
    if (isTerminal(s.nome)) return transitionMap.has(s.id)
    return true
  })

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Commento stato corrente */}
      {commentoStato && (
        <div style={{
          marginBottom: '8px', padding: '6px 10px',
          background: token.colorWarningBg, border: `1px solid ${token.colorWarningBorder}`,
          borderRadius: 4, fontSize: '12px', color: token.colorWarning,
          display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <WarningOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          <span><strong>Commento stato:</strong> {commentoStato}</span>
        </div>
      )}

      {/* Deroga sulla data di consegna — riquadro suo, perche' e' un'altra cosa
          dal commento di stato e sopravvive alle transizioni successive. */}
      {motivoDerogaData && (
        <div style={{
          marginBottom: '8px', padding: '6px 10px',
          background: token.colorWarningBg, border: `1px solid ${token.colorWarningBorder}`,
          borderRadius: 4, fontSize: '12px', color: token.colorWarning,
          display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <WarningOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          <span><strong>Ordinata senza data di consegna:</strong> {motivoDerogaData}</span>
        </div>
      )}

      {/* Motivo arretramento */}
      {motivazioneRifiuto && (
        <div style={{
          marginBottom: '8px', padding: '6px 10px',
          background: token.colorWarningBg, border: `1px solid ${token.colorWarningBorder}`,
          borderRadius: 4, fontSize: '12px', color: token.colorWarning,
          display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <RollbackOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          <span><strong>Motivo arretramento:</strong> {motivazioneRifiuto}</span>
        </div>
      )}

      {/* Stepper stati */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', marginBottom: auditTrail.length > 0 ? '10px' : 0 }}>
        {visibleStatuses.map((s, idx) => {
          const isCurrent = s.id === currentStatusId
          const transition = transitionMap.get(s.id)
          const isRicevuta = isRicevutaStatus(s.nome)
          const isClickable = !!transition && !isRicevuta

          const baseStyle: React.CSSProperties = {
            fontSize: '11px',
            height: '24px',
            padding: '0 8px',
            borderRadius: 12,
            lineHeight: '22px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            whiteSpace: 'nowrap',
          }

          return (
            <React.Fragment key={s.id}>
              {idx > 0 && (
                <span style={{ fontSize: '10px', color: token.colorBorder, flexShrink: 0 }}>›</span>
              )}
              {isCurrent ? (
                <span style={{
                  ...baseStyle,
                  background: token.colorSuccess,
                  color: '#fff',
                  border: `2px solid ${token.colorSuccessActive}`,
                  fontWeight: 700,
                  boxShadow: `0 0 0 2px ${token.colorSuccessBg}`,
                }}>
                  <CheckCircleOutlined style={{ fontSize: 10 }} />
                  {s.nome}
                </span>
              ) : isClickable ? (
                <Tooltip title={transition!.requires_reason ? 'Richiede motivazione' : `Sposta in: ${s.nome}`}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleTransitionClick(transition!)}
                    loading={transitionInProgress && selectedTransition?.to_status_id === s.id}
                    style={{ ...baseStyle, height: 24, border: 'none' }}
                  >
                    {s.nome}
                    {transition!.requires_reason && <LockOutlined style={{ fontSize: '9px', opacity: 0.7 }} />}
                  </Button>
                </Tooltip>
              ) : isRicevuta ? (
                <Tooltip title="La RDA passa a Ricevuta quando tutti i suoi articoli risultano ricevuti — dalla scheda lavoro o dalla Ricezione Ordini.">
                  <span style={{
                    ...baseStyle,
                    background: token.colorPrimaryBg,
                    color: token.colorPrimary,
                    border: `1px solid ${token.colorPrimaryBorder}`,
                    cursor: 'default',
                  }}>
                    {s.nome}
                  </span>
                </Tooltip>
              ) : (
                <span style={{
                  ...baseStyle,
                  background: token.colorPrimaryBg,
                  color: token.colorPrimary,
                  border: `1px solid ${token.colorPrimaryBorder}`,
                  opacity: 0.5,
                  cursor: 'default',
                }}>
                  {s.nome}
                </span>
              )}
            </React.Fragment>
          )
        })}

        {visibleStatuses.length === 0 && (
          <span style={{ fontSize: '11px', color: token.colorTextQuaternary, fontStyle: 'italic' }}>
            Caricamento stati...
          </span>
        )}
      </div>

      {/* Errore */}
      {error && (
        <Alert message={error} type="error" closable onClose={() => setError(null)}
          style={{ marginBottom: '8px', padding: '4px 8px', fontSize: '12px' }} />
      )}


      {/* Modal motivo (requires_reason) */}
      <Modal
        title={`Motivo — ${selectedTransition?.to_status_name ?? ''}`}
        open={reasonModalVisible}
        onOk={() => form.submit()}
        onCancel={() => { setReasonModalVisible(false); form.resetFields() }}
        confirmLoading={transitionInProgress}
        width="min(400px, 95vw)"
      >
        <Form form={form} layout="vertical" onFinish={v => selectedTransition && performTransition(selectedTransition, v.reason, null)}>
          <Form.Item name="reason" label="Motivo"
            rules={[{ required: true, message: 'Inserire un motivo' }, { min: 5, message: 'Almeno 5 caratteri' }]}>
            <VoiceTextarea rows={3} minHeight={90} maxHeight={200} placeholder="Descrivi il motivo..." label="Motivo" compact />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal commento (richiede_commento_stato) */}
      <Modal
        title={`Commento — ${selectedTransition?.to_status_name ?? ''}`}
        open={commentoModalVisible}
        onOk={() => commentoForm.submit()}
        onCancel={() => { setCommentoModalVisible(false); commentoForm.resetFields() }}
        confirmLoading={transitionInProgress}
        width="min(400px, 95vw)"
      >
        <Form form={commentoForm} layout="vertical" onFinish={v => selectedTransition && performTransition(selectedTransition, null, v.commento_stato)}>
          <Form.Item name="commento_stato" label="Commento"
            rules={[{ required: true, message: 'Inserire un commento' }, { min: 3, message: 'Almeno 3 caratteri' }]}>
            <VoiceTextarea rows={3} minHeight={90} maxHeight={200} placeholder="Inserisci un commento per questo stato..." label="Commento" compact />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal deroga — Ordinata senza data consegna valida */}
      <Modal
        title={<span><WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Deroga — Ordinata senza data consegna</span>}
        open={derogaModalVisible}
        onOk={() => derogaForm.submit()}
        okText="Conferma ordine"
        onCancel={() => { setDerogaModalVisible(false); derogaForm.resetFields(); setSelectedTransition(null) }}
        confirmLoading={transitionInProgress}
        width="min(440px, 95vw)"
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Data consegna non impostata o già trascorsa"
          description="Per procedere senza una data di consegna futura è obbligatorio inserire un commento di deroga. Il commento verrà salvato e mostrato sulla RDO."
        />
        <Form
          form={derogaForm}
          layout="vertical"
          onFinish={v => selectedTransition && performTransition(selectedTransition, null, null, v.commento_deroga)}
        >
          <Form.Item
            name="commento_deroga"
            label="Motivazione deroga *"
            rules={[
              { required: true, message: 'La motivazione è obbligatoria' },
              { min: 10, message: 'Almeno 10 caratteri' },
            ]}
          >
            <VoiceTextarea
              rows={3}
              minHeight={90}
              maxHeight={200}
              placeholder="Es. Fornitore ha confermato verbalmente la consegna entro fine settimana..."
              label="Motivazione deroga"
              compact
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default RDAStateTransition
